import { refreshAccessToken } from "./Auth";

/**
 * Formats listing data before sending to API
 * 
 * Returns a new object with attributes and daa types matching those expected by the API
 * @param {Object} data - The listing object
 * @returns {Object} A new object containing the formatted listing data
 */
export const formatListingData = (data) => {
    if (!data) {
        return {};
    }

    return Object.entries(data).reduce((acc, [key, value]) => {
        if (key === "images") {
            acc[key] = value?.map( imageObj => imageObj.image) || []; // Extract image URLs
        } else {
            acc[key] = value;
        }

        return acc;
    }, {}); // {} is the initial value of the accumaltor
}


/**
 * Checks if a filter is empty
 * 
 * It iterates through each key-value pair in the filter to check for falsy values.
 * 
 * @param {Object} filter - An object containing filter criteria.
 * @returns {Boolean} - true if filter is empty, otherwise, false
 */
const isFilterEmpty = (filter) => {
    for (const key in filter) {
        if (filter[key]) {
            return false;
        }
    }
    return true;
};


/**
 * 
 * @param {Object} filters - An object containing filter criteria. Each key represents a listing field, and the value represents the filter value.
 * @param {Array<Object>} listings - An array of property listing objects.
 * @returns {Array<Object>} - A new array containing property listings.
 */
export const filterListings = (filters, listings) => {
    if (!listings) {
        return []; // Return empty array if no listings were provided
    }

    if (!filters) {
        return listings; // Return all listings if no filters were given
    }

    if (isFilterEmpty(filters)) {
        return listings; // Return all listings if no filter fields were selected
    }

    return listings.filter((listing) => {
        for (const key in filters) {
            if (filters[key]) { // Check that value exists in filter
                const filterValue = filters[key].toLowerCase();
                const listingValue = String(listing[key]).toLowerCase();

                if (key === "price") {
                    if (Number(listingValue) > Number(filterValue)) {
                        return false;
                    }
                } else if (key === "bedrooms" || key === "land_size") {
                    if (Number(listingValue) !== Number(filterValue)) {
                        return false;
                    }
                } else if (!listingValue.includes(filterValue)) {
                    return false; // Listing doesn't match the filter
                }
            }
        }
        return true;
    })
}

/**
 * fetchWithAuth - Makes API request while ensuring that authentication is handled seamlessly
 * @param  {String} url - url to fetch
 * @param {Object} options - request body
 * @returns {Response}: API response object
 */
export const fetchWithAuth = async (url, options = {}) => {
    try {
        const response = await fetch(url, {
            ...options,
            credentials: 'include',
        });

        if (response.status === 401) {
            const refreshed = await refreshAccessToken();
            if (refreshed) {
                console.log("Access token refreshed, retrying original request.");
                const retryResponse = await fetch(url, { ...options, credentials: 'include' });
                return retryResponse;
            } else {
                console.error("Failed to refresh token. The user needs to log in again.");
                // If the refresh fails, return the original 401 response so calling component can handle it
                return response;
                // Refresh failed, redirect admin to log in page
            }
        }
        return response;
    }
    catch (error) {
        console.error("API call failed", error);
        throw error;
    }
}

/**
 * extractErrorMessage - Returns the lowest level error message from an API response object
 * @param {Response} response - Response object
 * @returns {String} - Most accurate description of error
 */
export const extractErrorMessage = (response) => {
    // Case 1: Validation errors (400)
    if (response.errors) {
        // Flatten the first error we find
        const firstField = Object.keys(response.errors)[0];
        if (firstField && response.errors[firstField].length > 0) {
            return response.errors[firstField][0];
        }

    // Case 2: Server error with "detail" (500 or other exceptions)
    } else if (response.detail) {
        return response.detail;

    // Case 3: Custom error message
    } else if (response.error) {
        return response.error;

    // Case 4: Generic message from backend
    } else if (response.message) {
        // Fallback to DRF's top-level message
        return response.message;
    }
}

/**
 * createQueryParams - Converts form filters to query parameters
 * @param {Object} filters - An object containing filter criteria. Each key represents a listing field, and the value represents the filter value.
 * @returns {String} - A query params string
 */
export const createQueryParams = (filters) => {
    const lookupMap = {
        'property_type': 'icontains',
        'property_name': 'icontains',
        'price': 'lte',
        'state': 'exact',
        'city': 'exact',
        'land_use': 'icontains',
        'land_title': 'icontains',
        'bedrooms': 'exact',
        'bathrooms': 'exact',
        'land_size': 'gte',
        'terrace': 'gte',
        'serviced': 'exact',
        'status': 'exact',
        'newly_built': 'exact',
        'furnished': 'exact',
        'youtube_url': 'exact'
    }

    const queryParams = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
        // Skip empty or null values
        if (value === null || value === "") {
            return;
        }

        // Get the lookup from the map or default to exact
        const lookup = lookupMap[key] || 'exact';
        const paramKey = `${key}${lookup === 'exact' ? '' : '__' + lookup}`;
        queryParams.append(paramKey, value);
    });

    return queryParams.toString();
}

/**
 * formatLandSize - Formats a plot size number into a human-readable string with 'sqm'
 * @param {Number} value - Plot size to be formatted
 * @returns {String} - Formatted land size string
 */
export const formatLandSize = (value) => {
    if (!value) return '';
    return value.toString().replace(/\D/g, '') + ' sqm';
};

/**
 * formatPrice - Formats a number into a human-readable string with a currency symbol and comma separators
 * @param {Number} value - Number to be formatted
 * @returns {String} - Formatted currency string
 */
export const formatPrice = (value) => {
    if (!value) return '';

    // Create an Intl.NumberFormat instance for Nigerian Naira
    const formatter = new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    // Format the number and remove the currency code (NGN)
    return formatter.format(value).replace('NGN', '₦');
};

/**
 * sanitizeString - Sanitize a string and removes all non-digit characters
 * @param {String} - String to be sanitized
 * @returns {Number} - Sanitized number
 */
export const sanitizeString = (value) => {
    const sanitizedString = value.toString().replace(/\D/g, '');
    return sanitizedString ? Number(sanitizedString) : null;
};

/**
 * normalizeFilters - Converts filter parameters to API suitable format
 * @param {Object} filters - Filter
 * @returns {Object} - Normalized filter
 */
export const normalizeFilters = (filters) => {
    return Object.fromEntries(
        Object.entries(filters).map(([key, value]) => {
            if (!value) return [key, null]; // empty -> null
            if (key === 'bedrooms' || key === 'price') return [key, Number(value)]; // cast to number
            return [key, value]; // leave as is
        })
    )
}